import { LoadingConstants, useLogger } from '@zg-rentals/rental-manager-env';
import React from 'react';
import { BillingPortalStateContext } from '../providers/BillingPortalStateProvider';
import type { UserZuoraPaymentMethodDto } from '@zg-rentals/ts-adproducts-client';
import {
  AdproductsUserBillingInvoicePaymentApiHandler_deletePaymentMethodV2,
  AdproductsUserBillingInvoicePaymentApiHandler_getPaymentMethodsForAccount,
  AdproductsUserBillingInvoicePaymentApiHandler_importPaymentMethods,
  AdproductsUserBillingInvoicePaymentApiHandler_removeAutopay,
  AdproductsUserBillingInvoicePaymentApiHandler_saveDefaultPaymentMethodaAndEnableAutopay,
} from '@zg-rentals/ts-adproducts-client';

export interface BillingPortalBannerStateType {
  displaySuccessMessage: boolean;
}

export interface AccountPaymentMethodState {
  paymentMethodLock: boolean;
  accountPaymentMethods: Array<UserZuoraPaymentMethodDto>;
  isAutopayEnabled: boolean;
  autopayMethodId: string | null;
  defaultPaymentMethod: UserZuoraPaymentMethodDto | null;
  accountName: string;
  loadingState: string;
}

const initState = () => ({
  loadingState: LoadingConstants.INITIAL,
  paymentMethodLock: false,
  accountPaymentMethods: [],
  isAutopayEnabled: false,
  autopayMethodId: null,
  defaultPaymentMethod: null,
  accountName: '',
});

export const _useAccountPaymentMethods = () => {
  const [paymentMethodState, setPaymentMethodState] = React.useState<AccountPaymentMethodState>(initState());
  const logger = useLogger();
  const resetState = () => setPaymentMethodState(initState());

  const setPaymentMethodLock = (isLocked: boolean) =>
    setPaymentMethodState({ ...paymentMethodState, paymentMethodLock: isLocked });

  const importZuoraPaymentMethods = (accountNumber: string) =>
    AdproductsUserBillingInvoicePaymentApiHandler_importPaymentMethods({ accountNumber });

  const deleteZuoraPaymentMethod = (accountNumber: string, paymentMethodId: string) =>
    AdproductsUserBillingInvoicePaymentApiHandler_deletePaymentMethodV2({ accountNumber, paymentMethodId });

  const removeZuoraAutoPay = (accountNumber: string) => {
    return AdproductsUserBillingInvoicePaymentApiHandler_removeAutopay({ accountNumber }).catch(() => {
      logger.error(`Failed to remove autopay for ${accountNumber}`);
      throw new Error();
    });
  };

  const saveDefaultZuoraPaymentMethod = (params: {
    autopayEnabled: boolean;
    defaultPaymentMethodId: string;
    version?: string;
    accountNumber: string;
  }) =>
    AdproductsUserBillingInvoicePaymentApiHandler_saveDefaultPaymentMethodaAndEnableAutopay(params).catch(() => {
      logger.error(`Failed to remove autopay for ${params}`);
    });

  const getAccountPaymentMethods = (accountNumber: string) => {
    setPaymentMethodState({
      ...paymentMethodState,
      loadingState: LoadingConstants.LOADING,
    });
    return AdproductsUserBillingInvoicePaymentApiHandler_getPaymentMethodsForAccount({ accountNumber })
      .then((result) => {
        return setPaymentMethodState((lastState) => {
          const paymentMethods = result.paymentMethods ? result.paymentMethods : [];
          return {
            ...lastState,
            loadingState: LoadingConstants.SUCCESS,
            accountPaymentMethods: paymentMethods,
            paymentMethodLock: result.hasPaymentMethodLock,
            autopayOwnerId: result.autopayOwnerId,
            isAutopayEnabled: result.isAutopayEnabled,
            autopayMethodId: result.autopayMethodId,
            defaultPaymentMethod: result.defaultPaymentMethod,
            accountName: result.accountName,
          };
        });
      })
      .catch(() =>
        setPaymentMethodState((lastState) => ({
          ...lastState,
          loadingState: LoadingConstants.ERROR,
        })),
      );
  };

  return {
    paymentMethodState,
    getAccountPaymentMethods,
    resetState,
    importZuoraPaymentMethods,
    setPaymentMethodLock,
    deleteZuoraPaymentMethod,
    removeZuoraAutoPay,
    saveDefaultZuoraPaymentMethod,
  };
};

export const useAccountPaymentMethods = () => {
  const billingPortalState = React.useContext(BillingPortalStateContext);

  if (!billingPortalState) throw new Error('State Not initialized');

  return billingPortalState.accountPaymentMethods;
};
